import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Per-user learning shelf. Reads from learning_resources scoped via
// RLS to the caller. Topic + type filters happen at query time so
// the user's full shelf doesn't stream back when they only want one
// topic's videos.
//
// Returns the materialised list plus add / update / remove / toggle
// helpers. All writes are optimistic — the local list updates
// immediately, with a rollback on error.

export function useLearningResources({ topic, type } = {}) {
  const { user } = useAuth()
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    let q = supabase
      .from('learning_resources')
      .select('id, topic, title, url, type, notes, source_host, image_url, completed, added_at')
      .order('added_at', { ascending: false })
      .limit(500)
    if (topic) q = q.eq('topic', topic)
    if (type && type !== 'all') q = q.eq('type', type)
    const { data, error: err } = await q
    if (err) setError(err.message)
    else setResources(data || [])
    setLoading(false)
  }, [user, topic, type])

  useEffect(() => { fetch() }, [fetch])

  // List of distinct topics across the user's shelf. Drives the
  // topic chip row at the top of the page.
  const [allTopics, setAllTopics] = useState([])
  useEffect(() => {
    if (!supabaseConfigured || !user) return
    let alive = true
    supabase
      .from('learning_resources')
      .select('topic')
      .order('added_at', { ascending: false })
      .limit(1000)
      .then(({ data }) => {
        if (!alive) return
        const set = new Set((data || []).map((r) => r.topic).filter(Boolean))
        setAllTopics(Array.from(set))
      })
    return () => { alive = false }
  }, [user, resources])

  const add = useCallback(async (input) => {
    if (!user) return null
    const row = {
      user_id: user.id,
      topic: (input.topic || '').trim(),
      title: (input.title || '').trim(),
      url: (input.url || '').trim(),
      type: input.type || 'other',
      notes: input.notes || null,
      source_host: input.source_host || null,
      image_url: input.image_url || null,
    }
    if (!row.topic || !row.title || !row.url) return null
    const { data, error: err } = await supabase
      .from('learning_resources')
      .insert(row)
      .select()
      .single()
    if (err) {
      setError(err.message)
      return null
    }
    // Optimistic-ish: prepend the saved row so the page reflects
    // the new resource without a full refetch.
    if (data && (!topic || data.topic === topic) && (!type || type === 'all' || data.type === type)) {
      setResources((prev) => [data, ...prev])
    }
    return data
  }, [user, topic, type])

  const remove = useCallback(async (id) => {
    const prev = resources
    setResources((curr) => curr.filter((r) => r.id !== id))
    const { error: err } = await supabase
      .from('learning_resources')
      .delete()
      .eq('id', id)
    if (err) {
      setError(err.message)
      setResources(prev) // rollback
    }
  }, [resources])

  const toggleCompleted = useCallback(async (id) => {
    const row = resources.find((r) => r.id === id)
    if (!row) return
    const next = !row.completed
    setResources((curr) => curr.map((r) => (r.id === id ? { ...r, completed: next } : r)))
    const { error: err } = await supabase
      .from('learning_resources')
      .update({ completed: next, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (err) {
      setError(err.message)
      setResources((curr) => curr.map((r) => (r.id === id ? { ...r, completed: !next } : r)))
    }
  }, [resources])

  const counts = useMemo(() => {
    const out = { total: resources.length, completed: 0 }
    for (const r of resources) if (r.completed) out.completed += 1
    return out
  }, [resources])

  return {
    resources,
    allTopics,
    loading,
    error,
    counts,
    refetch: fetch,
    add,
    remove,
    toggleCompleted,
  }
}
